import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { DataGrid } from '@mui/x-data-grid';
import { Link as RouterLink } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Divider,
  CircularProgress,
  Grid,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import IconButton from '@mui/material/IconButton';
import LaunchIcon from '@mui/icons-material/Launch';
import { useNavigate } from 'react-router-dom';

const industryNames = {
  insurance: 'Insurance',
  healthcare: 'Healthcare',
  education: 'Education',
  'small-industries': 'Small Industries',
  research: 'Research',
  travel: 'Travel',
  'real-estate': 'Real Estate',
  finance: 'Finance',
  banking: 'Banking',
  legal: 'Legal',
  it: 'IT',
};

const IndustryDetails = () => {
  const navigate = useNavigate();
  const [pageSize, setPageSize] = useState(10);  // Default to 10 rows per page
  const [page, setPage] = useState(0);

  const { name } = useParams();
  const readableName = industryNames[name] || 'Industry';
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opportunities, setOpportunities] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      const token = localStorage.getItem('token');
      setLoading(true);
      try {
        const [oppsRes, provRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/opportunities/getOpportunities_industrypresence?industry=${name}`,
            {
              headers: { Authorization: `Bearer ${token}` }
            }
          ),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/industries_providers/${name}`),
        ]);
        setOpportunities(oppsRes.data);
        setProviders(provRes.data.providers);
      } catch (error) {
        console.error('Error fetching data', error);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [name]);

  const stripHtml = (html) => {
      const div = document.createElement('div');
      div.innerHTML = html;
      return div.textContent || div.innerText || '';
    };

  const columns = [
    { field: 'opportunity_number', headerName: 'Number', width: 120},
    { field: 'role', headerName: 'Role', width: 180 },
    { field: 'company', headerName: 'Company', width: 180 },
    {
      field: 'comments',
      headerName: 'Comments',
      width: 280,
      renderCell: (params) => {
        const plainText = stripHtml(params.value || '');
        const preview = plainText.split(' ').slice(0, 3).join(' ') + '...';
        return (
          <Typography variant="body2" noWrap title={plainText}>
            {preview}
          </Typography>
        );
      },
    },
    { field: 'link', headerName: 'Link', width: 180 },
    { field: 'noapplications', headerName: 'Applications', width: 180 },
    {
      field: 'apply',
      headerName: 'Apply',
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() =>
            navigate(`/opportunities?opportunity_number=${params.row.opportunity_number}`)
          }
        >
          <LaunchIcon />
        </IconButton>
      ),
    },
  ];

  // const rows = opportunities.map((opp, idx) => ({
  //   number: opp.opportunity_number,
  //   role: opp.role,
  //   company: opp.company,
  //   comments: opp.comments,
  //   link: opp.comments
  // }));

  return (
    <Box
    sx={{
      p: { xs: 2, sm: 3, md: 4 },
      background: 'linear-gradient(135deg, #f5f7fa, #c3cfe2)',
      minHeight: '100vh',
      width: '100%',
      margin: '0 auto',
    }}
  >
    <motion.div
      initial={{ opacity: 0, y: -40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ color: '#1a237e', mb: 1 }}>
        Welcome to {readableName}
      </Typography>
      <Typography variant="subtitle1" textAlign="center" color="text.secondary" mb={4}>
        Discover live opportunities and connect with top contributors in the {readableName} domain.
      </Typography>
    </motion.div>
  
    {loading ? (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    ) : (
      <Box mt={2}>
        <Grid container spacing={2}>
          {/* Active Providers */}
          <Grid item xs={12} sm={8}>
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Paper elevation={6} sx={{ p: 3, borderRadius: 3, height: '100%', boxShadow: 3 }}>
                <Typography fontWeight="bold" gutterBottom>
                 Active Providers from different companies:
                </Typography>
                <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                  {providers.map((association, index) => (
                    <ListItem key={index}>
                      <ListItemText primary={index+1 + '. '+ association} />
                    </ListItem>
                  ))}
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body2" fontWeight="bold">
                  Total Associations: {providers.length}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
  
          {/* Help Card */}
          <Grid item xs={12} sm={4}>
            <motion.div
              whileHover={{ scale: 1.03 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              <Paper
                elevation={6}
                sx={{
                  p: 3,
                  borderRadius: 4,
                  background: 'linear-gradient(to right, #fce4ec, #f06292)',
                  color: '#fff',
                  textAlign: 'center',
                  height: '100%',
                  boxShadow: 4,
                }}
              >
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                  <HelpOutlineIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Need Help?
                </Typography>
                <Typography>
                  <Link to="/request" style={{ color: '#fff', fontWeight: 'bold', textDecoration: 'underline' }}>
                    Raise a Request
                  </Link>
                </Typography>
                <Typography variant="body2" mt={1}>
                  Our providers will respond quickly if support is available.
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
  
        {/* Opportunities Card */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 3,
              borderRadius: 4,
              backgroundColor: '#fff',
              boxShadow: '0 8px 16px rgba(0,0,0,0.08)',
              mt: 4,
            }}
          >
            <Box mb={1}>
              <Typography
                variant="h6"
                fontWeight="bold"
                color="primary"
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <WorkOutlineIcon sx={{ mr: 1 }} />
                Latest Opportunities
              </Typography>
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                Click "Apply" to proceed or go to the{' '}
                <Link component={RouterLink} to="/opportunities" underline="hover" color="primary" fontWeight="bold">
                  Opportunities
                </Link>{' '}
                page and search by opportunity number for more details.
              </Typography>
            </Box>
            {opportunities.length === 0 ? (
              <Typography>No opportunities posted yet.</Typography>
            ) : (
              <Box sx={{ height: 400, width: '100%' }}>
                <DataGrid
                  rows={opportunities}
                  columns={columns}
                  pageSizeOptions={[5, 10, 15]}
                  paginationModel={{ pageSize, page }}
                  onPaginationModelChange={(model) => {
                    setPage(model.page);
                    setPageSize(model.pageSize);
                  }}
                  disableSelectionOnClick
                  sx={{
                    '& .MuiDataGrid-columnHeaders': {
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                    },
                  }}
                />
              </Box>
            )}
            <Box textAlign="right" mt={2}>
              <Link
                to="/opportunities"
                style={{
                  textDecoration: 'none',
                  fontWeight: 'bold',
                  color: '#1976d2',
                }}
              >
                View All Opportunities →
              </Link>
            </Box>
          </Paper>
        </motion.div>
      </Box>
    )}
  </Box>
  
  );
};

export default IndustryDetails;
